import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import { useAssetConfig } from "@/hooks/useAssetConfig";
import api from "@/lib/api";
import {
  LayoutDashboard, Search, Activity, RefreshCw, Plus, Hand, CheckCircle2,
  X, Truck, Fuel, Zap, MapPin, HardHat, Clock, Send, ChevronRight,
  BarChart3, Users, Timer, MessageSquare, AlertTriangle, Trash2,
  ArrowLeft, LogOut, Loader2, ChevronDown, Sparkles, Coffee, Upload,
  Navigation, Wrench, Settings, ArrowRightLeft, FileText, Download,
  ThumbsUp, ThumbsDown, BookOpen, CalendarDays, Route, Bell, PlusCircle, Bird
} from "lucide-react";
import LiveFeed from "@/components/shared/LiveFeed";
import DashboardSwitcher from "@/components/shared/DashboardSwitcher";
import LiveMap from "@/components/shared/LiveMap";
import DataImport from "@/components/planner/DataImport";
import TutorialLedger, { TutorialToggle } from "@/components/shared/TutorialLedger";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher";
import AnalyticsDashboard from "@/components/planner/AnalyticsDashboard";
import SubjectDetail from "@/components/planner/SubjectDetail";
import HubAdmin from "@/components/planner/HubAdmin";
import { getMachineHighlight, getSiteHighlight, highlightBorder, highlightIcon, highlightMachineBadge } from "@/lib/highlights";

const REGIONS = ["Antwerpen", "Brussel", "Oost-Vlaanderen", "West-Vlaanderen", "Wallonie"];

export default function PlannerDashboard() {
  const { session, setSession, hubData, refreshData, logout, loading, auth } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const ac = useAssetConfig(session);
  const nav = useNavigate();
  const { sites, machines, drivers, requests, events, feedback } = hubData;

  const [activeRegion, setActiveRegion] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toast, setToast] = useState(null);
  const [reviewDriver, setReviewDriver] = useState(null);
  const [pingMessage, setPingMessage] = useState("");
  const [expandedSite, setExpandedSite] = useState(null);
  const [funMode, setFunMode] = useState(false);
  const [funText, setFunText] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  // New feature states
  const [showAddSite, setShowAddSite] = useState(false);
  const [showAddMachine, setShowAddMachine] = useState(null); // site_id
  const [showCalendar, setShowCalendar] = useState(false);
  const [showRouteBuilder, setShowRouteBuilder] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [newSite, setNewSite] = useState({ name: "", city: "", region: "Antwerpen", gps_lat: 0, gps_lon: 0 });
  const [newMachine, setNewMachine] = useState({ machine_number: "", name: "", brand: "" });
  const [routeSites, setRouteSites] = useState([]);
  const [routeDriver, setRouteDriver] = useState("");
  const [routeName, setRouteName] = useState("");
  const [calForm, setCalForm] = useState({ event_type: "APPOINTMENT", title: "", date: "", time: "", assigned_to: "", description: "" });
  const [calNotifCount, setCalNotifCount] = useState(0);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showHubAdmin, setShowHubAdmin] = useState(false);
  const [subjectDetail, setSubjectDetail] = useState(null); // { type: "site"|"machine"|"driver", subject }

  // Direct Request state (for hubs without machinist)
  const [directReqSiteId, setDirectReqSiteId] = useState(null);
  const [directReqMachine, setDirectReqMachine] = useState("");
  const [directReqType, setDirectReqType] = useState("");
  const [directReqNotes, setDirectReqNotes] = useState("");
  const [directReqSubmitting, setDirectReqSubmitting] = useState(false);

  // Transport approval state
  const [transportApproveId, setTransportApproveId] = useState(null);
  const [transportDriver, setTransportDriver] = useState("");
  const [showMap, setShowMap] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    showToast(t("data_synced"));
    setIsRefreshing(false);
  };

  const handleHighFive = async (to) => {
    await api.sendHighFive({ from_user: session.userName || "Planner", to_user: to, hub_id: session.hubId });
    showToast(t("high_five_sent"));
    refreshData();
  };

  const handleSendPing = async (driverName) => {
    if (!pingMessage.trim()) return;
    await api.sendMessage({
      hub_id: session.hubId, from_user: session.userName || "Planner",
      to_user: driverName, message: pingMessage, msg_type: "PING"
    });
    showToast(t("ping_sent"));
    setPingMessage("");
    refreshData();
  };

  const handleApprove = async (reqId, driverName) => {
    await api.approveRequest(reqId, driverName);
    showToast(t("approve") + "!");
    refreshData();
  };

  const handleConfirmTransport = async (reqId) => {
    await api.confirmTransport(reqId);
    showToast("Transport confirmed! Machine moved.");
    refreshData();
  };

  const generatePDF = async (type, subject) => {
    const { jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");
    const doc = new jsPDF();
    const now = new Date().toLocaleDateString();
    const hubName = session?.hubName || "Hub";

    doc.setFontSize(18);
    doc.setTextColor(30);

    if (type === "site-log" && subject) {
      doc.text(`Site Log: ${subject.name}`, 14, 22);
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(`${subject.city} / ${subject.region} | ${now} | Pigeon Supply`, 14, 30);
      doc.text(`Phase: ${subject.current_stage || "N/A"} | GPS: ${subject.gps_lat > 0 ? `${subject.gps_lat.toFixed(4)}, ${subject.gps_lon.toFixed(4)}` : "N/A"}`, 14, 36);

      const siteMachines = machines.filter(m => m.site_id === subject.id);
      const siteReqs = requests.filter(r => r.site_id === subject.id);
      const siteEvents = events.filter(e => e.site_id === subject.id).slice(0, 50);

      autoTable(doc, { startY: 44, head: [["Machine #", "Name", "Brand", "Status", "Fuel %"]], body: siteMachines.map(m => [m.machine_number, m.name, m.brand || "-", m.status_type, `${m.fuel_level_percent}%`]), styles: { fontSize: 8 }, headStyles: { fillColor: [59, 130, 246] } });
      let y = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11); doc.setTextColor(30); doc.text("Requests", 14, y); y += 6;
      autoTable(doc, { startY: y, head: [["Type", "Machine", "Status", "Urgency", "Date"]], body: siteReqs.map(r => [r.request_type, r.machine_number, r.status, r.urgency, new Date(r.created_at).toLocaleDateString()]), styles: { fontSize: 7 }, headStyles: { fillColor: [132, 204, 22] } });
      y = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11); doc.setTextColor(30); doc.text("Activity Log", 14, y); y += 6;
      autoTable(doc, { startY: y, head: [["Time", "Event", "Message"]], body: siteEvents.map(e => [new Date(e.timestamp).toLocaleString(), e.event_type, e.message?.substring(0, 80)]), styles: { fontSize: 7 }, headStyles: { fillColor: [100, 116, 139] } });

      doc.save(`Site_${subject.name}_log_${now.replace(/\//g, "-")}.pdf`);
      showToast("Site log exported!");
      return;
    }

    if (type === "machine-log" && subject) {
      const mSite = sites.find(s => s.id === subject.site_id);
      doc.text(`Machine Log: ${subject.machine_number}`, 14, 22);
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(`${subject.name} | ${subject.brand || "N/A"} | Site: ${mSite?.name || "N/A"} | ${now}`, 14, 30);
      doc.text(`Status: ${subject.status_type} | Fuel: ${subject.fuel_level_percent}% | Last Refuel: ${subject.last_refuel ? new Date(subject.last_refuel).toLocaleString() : "N/A"}`, 14, 36);

      const mReqs = requests.filter(r => r.machine_id === subject.id);
      const mEvents = events.filter(e => e.machine_id === subject.id || e.machine_number === subject.machine_number).slice(0, 50);

      autoTable(doc, { startY: 44, head: [["Type", "Status", "Urgency", "Notes", "Date"]], body: mReqs.map(r => [r.request_type, r.status, r.urgency, r.notes?.substring(0, 60) || "-", new Date(r.created_at).toLocaleDateString()]), styles: { fontSize: 8 }, headStyles: { fillColor: [245, 158, 11] } });
      let y = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11); doc.setTextColor(30); doc.text("Activity Log", 14, y); y += 6;
      autoTable(doc, { startY: y, head: [["Time", "Event", "Message"]], body: mEvents.map(e => [new Date(e.timestamp).toLocaleString(), e.event_type, e.message?.substring(0, 80)]), styles: { fontSize: 7 }, headStyles: { fillColor: [100, 116, 139] } });

      doc.save(`Machine_${subject.machine_number}_log_${now.replace(/\//g, "-")}.pdf`);
      showToast("Machine log exported!");
      return;
    }

    if (type === "driver-log" && subject) {
      const stats = driverStats[subject] || {};
      const driverFb = (feedback || []).filter(f => f.driver_name === subject);
      const driverEvents = events.filter(e => e.driver_name === subject).slice(0, 50);
      doc.text(`Driver Log: ${subject}`, 14, 22);
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(`${now} | Pigeon Supply`, 14, 30);
      doc.text(`Diesel: ${stats.liters || 0}L | Tasks: ${stats.machines || 0} | AdBlue: ${stats.adblue || 0}L`, 14, 36);

      autoTable(doc, { startY: 44, head: [["Time", "Event", "Message"]], body: driverEvents.map(e => [new Date(e.timestamp).toLocaleString(), e.event_type, e.message?.substring(0, 80)]), styles: { fontSize: 7 }, headStyles: { fillColor: [239, 68, 68] } });
      if (driverFb.length > 0) {
        let y = doc.lastAutoTable.finalY + 8;
        doc.setFontSize(11); doc.setTextColor(30); doc.text("Feedback", 14, y); y += 6;
        autoTable(doc, { startY: y, head: [["Type", "Incident", "Site", "Text"]], body: driverFb.map(f => [f.type, f.incident_type || "-", f.site_name || "-", f.text?.substring(0, 60) || "-"]), styles: { fontSize: 7 }, headStyles: { fillColor: [132, 204, 22] } });
      }
      doc.save(`Driver_${subject}_log_${now.replace(/\//g, "-")}.pdf`);
      showToast("Driver log exported!");
      return;
    }

    doc.text(`${hubName} - ${type.toUpperCase()} LOG`, 14, 22);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Generated: ${now} | Pigeon Supply`, 14, 30);

    if (type === "sites") {
      autoTable(doc, {
        startY: 38,
        head: [["Code", "Name", "City", "Region", "GPS", "Machines", "Open Requests"]],
        body: sites.map(s => [
          s.code, s.name, s.city, s.region,
          s.gps_lat > 0 ? `${s.gps_lat.toFixed(3)},${s.gps_lon.toFixed(3)}` : "-",
          machines.filter(m => m.site_id === s.id).length,
          requests.filter(r => r.site_id === s.id && r.status !== "DONE").length,
        ]),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [132, 204, 22] },
      });
    } else if (type === "machines") {
      autoTable(doc, {
        startY: 38,
        head: [["Number", "Name", "Site", "Status", "Brand", "Fuel%", "GPS"]],
        body: machines.map(m => {
          const site = sites.find(s => s.id === m.site_id);
          return [
            m.machine_number, m.name, site?.name || "-", m.status_type || "OK",
            m.brand || "-", `${m.fuel_level_percent}%`,
            m.gps_lat > 0 ? `${m.gps_lat.toFixed(3)},${m.gps_lon.toFixed(3)}` : "-"
          ];
        }),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [132, 204, 22] },
      });
    } else if (type === "drivers") {
      autoTable(doc, {
        startY: 38,
        head: [["Name", "Region", "Status", "High Fives", "Diesel Delivered", "Tasks Done"]],
        body: drivers.map(d => {
          const stats = driverStats[d.driver_name] || {};
          return [d.driver_name, d.region, d.status, d.high_five_count || 0, `${stats.liters || 0}L`, stats.machines || 0];
        }),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [132, 204, 22] },
      });
    }

    doc.save(`${hubName}_${type}_${now.replace(/\//g, "-")}.pdf`);
    showToast(`${type} PDF exported!`);
  };

  const handleRemoveDriver = async (name) => {
    await api.deleteDriver(name, session.hubId);
    showToast(`${name} removed`);
    setReviewDriver(null);
    refreshData();
  };

  const handleFunRequest = async () => {
    if (!funText.trim()) return;
    await api.createRequest({
      hub_id: session.hubId, machine_id: "FUN", machine_number: "FUN-001",
      site_id: sites[0]?.id || "FUN", site_name: "Fun Request",
      urgency: "NONE", products: ["FUN"], notes: funText,
      requester_type: "PLANNER", is_fun_request: true, fun_message: funText
    });
    showToast("Fun request sent!");
    setFunText("");
    setFunMode(false);
    refreshData();
  };

  const handleDirectRequest = async (siteId) => {
    if (!directReqMachine) return;
    setDirectReqSubmitting(true);
    const site = sites.find(s => s.id === siteId);
    const machine = machines.find(m => m.id === directReqMachine);
    const reqType = directReqType || ac.requestType;
    try {
      await api.createRequest({
        hub_id: session.hubId,
        machine_id: machine?.id || directReqMachine,
        machine_number: machine?.machine_number || directReqMachine,
        site_id: siteId,
        site_name: site?.name || siteId,
        request_type: reqType,
        urgency: "NONE",
        products: [],
        notes: directReqNotes || `${reqType} requested by ${session?.userName || "Planner"}`,
        requester_type: "PLANNER",
        current_fuel_level: 0,
        gps_lat: site?.gps_lat || 0,
        gps_lon: site?.gps_lon || 0,
      });
      showToast(`${ac.requestLabel} created!`);
      setDirectReqSiteId(null);
      setDirectReqMachine("");
      setDirectReqType("");
      setDirectReqNotes("");
      refreshData();
    } catch (e) { console.error(e); showToast("Failed to create request"); }
    setDirectReqSubmitting(false);
  };

  // Fetch calendar events and routes
  const loadCalendarAndRoutes = async () => {
    try {
      const [cal, rts] = await Promise.all([
        api.getCalendarEvents(session.hubId),
        api.getRoutes(session.hubId),
      ]);
      setCalendarEvents(cal);
      setRoutes(rts);
      const pending = cal.filter(e => e.event_type === "LEAVE_REQUEST" && e.status === "PENDING");
      setCalNotifCount(pending.length);
    } catch (e) { console.error(e); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCalendarAndRoutes(); }, []);

  // Add Site
  const handleAddSite = async () => {
    if (!newSite.name.trim()) return;
    await api.createSite({ hub_id: session.hubId, ...newSite, code: newSite.name.substring(0, 4).toUpperCase() });
    showToast("Site added!");
    setNewSite({ name: "", city: "", region: "Antwerpen", gps_lat: 0, gps_lon: 0 });
    setShowAddSite(false);
    refreshData();
  };

  const handleDeleteSite = async (siteId) => {
    await api.deleteSite(siteId);
    showToast("Site removed from pool");
    refreshData();
  };

  // Add Machine
  const handleAddMachine = async (siteId) => {
    if (!newMachine.machine_number.trim()) return;
    await api.createMachine({ hub_id: session.hubId, site_id: siteId, ...newMachine });
    showToast("Machine added!");
    setNewMachine({ machine_number: "", name: "", brand: "" });
    setShowAddMachine(null);
    refreshData();
  };

  const handleDeleteMachine = async (machineId) => {
    await api.deleteMachine(machineId);
    showToast("Machine removed from pool");
    refreshData();
  };

  // Route Planning
  const handleCreateRoute = async () => {
    if (!routeDriver || routeSites.length === 0) return;
    await api.createRoute({ hub_id: session.hubId, name: routeName || `Route ${new Date().toLocaleDateString()}`, driver_name: routeDriver, site_ids: routeSites });
    showToast("Route assigned!");
    setRouteSites([]);
    setRouteDriver("");
    setRouteName("");
    setShowRouteBuilder(false);
    loadCalendarAndRoutes();
  };

  const handleDeleteRoute = async (routeId) => {
    await api.deleteRoute(routeId);
    showToast("Route deleted");
    loadCalendarAndRoutes();
  };

  // Calendar
  const handleCreateCalEvent = async () => {
    if (!calForm.title.trim() || !calForm.date) return;
    await api.createCalendarEvent({ hub_id: session.hubId, ...calForm, created_by: session.userName || "Planner" });
    showToast("Event created!");
    setCalForm({ event_type: "APPOINTMENT", title: "", date: "", time: "", assigned_to: "", description: "" });
    loadCalendarAndRoutes();
  };

  const handleApproveLeave = async (eventId, status) => {
    await api.approveLeave(eventId, status);
    showToast(`Leave ${status.toLowerCase()}`);
    loadCalendarAndRoutes();
  };

  const handleDeleteCalEvent = async (eventId) => {
    await api.deleteCalendarEvent(eventId);
    showToast("Event deleted");
    loadCalendarAndRoutes();
  };

  const handleToolboxUpload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("hub_id", session.hubId);
    fd.append("title", file.name);
    fd.append("date", new Date().toISOString().split("T")[0]);
    fd.append("created_by", session.userName || "Planner");
    await api.uploadToolbox(fd);
    showToast("Toolbox uploaded!");
    loadCalendarAndRoutes();
  };

  const driverStats = useMemo(() => {
    const stats = {};
    drivers.forEach(d => {
      const dReqs = requests.filter(r => r.assigned_driver === d.driver_name && r.status === "DONE");
      const total = requests.filter(r => r.assigned_driver === d.driver_name).length;
      stats[d.driver_name] = {
        liters: dReqs.reduce((s, r) => s + (r.delivered_diesel || 0), 0),
        adblue: dReqs.reduce((s, r) => s + (r.delivered_adblue || 0), 0),
        machines: dReqs.length,
        sites: new Set(dReqs.map(r => r.site_id)).size,
        efficiency: total > 0 ? Math.round((dReqs.length / total) * 100) : 0,
        tasks: dReqs.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))
      };
    });
    return stats;
  }, [drivers, requests]);

  const globalStats = useMemo(() => {
    const done = requests.filter(r => r.status === "DONE");
    return {
      totalLiters: done.reduce((s, r) => s + (r.delivered_diesel || 0), 0),
      totalMachines: done.length,
      activeDrivers: drivers.filter(d => d.status === "ACTIVE").length,
      newRequests: requests.filter(r => r.status === "NEW").length
    };
  }, [requests, drivers]);

  const filteredSites = useMemo(() => {
    return sites.filter(s => {
      const matchRegion = activeRegion === "ALL" || s.region === activeRegion;
      const matchSearch = (s.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (s.city || "").toLowerCase().includes(searchQuery.toLowerCase());
      return matchRegion && matchSearch;
    });
  }, [sites, activeRegion, searchQuery]);

  const getSiteRequests = (siteId) => requests.filter(r => r.site_id === siteId);
  const getSiteMachines = (siteId) => machines.filter(m => m.site_id === siteId);

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col" data-testid="planner-dashboard">
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[1000] bg-lime-500 text-slate-950 px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 font-bold text-sm uppercase tracking-wider animate-slideInDown">
          <CheckCircle2 className="w-4 h-4" /> {toast}
        </div>
      )}

      <TutorialLedger role="planner" isOpen={showTutorial} onClose={() => setShowTutorial(false)} />

      {/* Analytics Overlay */}
      {showAnalytics && <AnalyticsDashboard hubId={session.hubId} session={session} onClose={() => setShowAnalytics(false)} />}

      {/* Hub Admin Overlay */}
      {showHubAdmin && <HubAdmin hubId={session.hubId} onClose={() => setShowHubAdmin(false)} />}

      {/* Subject Detail Overlay */}
      {subjectDetail && (
        <SubjectDetail
          type={subjectDetail.type}
          subject={subjectDetail.subject}
          hubData={{ sites, machines, requests, events, drivers, feedback }}
          onClose={() => setSubjectDetail(null)}
          onExportPDF={() => {
            if (subjectDetail.type === "site") generatePDF("site-log", subjectDetail.subject);
            if (subjectDetail.type === "machine") generatePDF("machine-log", subjectDetail.subject);
            if (subjectDetail.type === "driver") generatePDF("driver-log", subjectDetail.subject);
          }}
        />
      )}

      {/* Driver Review Modal */}
      {reviewDriver && (
        <div className="fixed inset-0 z-[200] bg-white/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn" data-testid="driver-review-modal">
          <div className="bg-slate-50 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-slate-200 animate-scaleIn">
            <header className="bg-white p-6 flex justify-between items-center border-b border-slate-200">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-white font-black text-xl ${reviewDriver.status === "ACTIVE" ? "bg-lime-500 text-slate-950" : "bg-slate-700"}`}>
                  {reviewDriver.driver_name[0]}
                </div>
                <div>
                  <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">{reviewDriver.driver_name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`status-dot ${reviewDriver.status === "ACTIVE" ? "online" : "offline"}`} />
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{reviewDriver.region}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleHighFive(reviewDriver.driver_name)} className="p-3 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl transition-all active:scale-95" data-testid="modal-highfive-btn">
                  <Hand className="w-5 h-5" />
                </button>
                <button onClick={() => setSubjectDetail({ type: "driver", subject: reviewDriver.driver_name })} className="p-3 bg-slate-100 hover:bg-slate-200 text-amber-400 rounded-xl transition-all active:scale-95" data-testid="driver-log-btn">
                  <Download className="w-5 h-5" />
                </button>
                <button onClick={() => setReviewDriver(null)} className="p-3 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl transition-all" data-testid="close-driver-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </header>
            <div className="p-6 overflow-y-auto max-h-[70vh] grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-lime-500" /> Performance
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 bg-white border border-slate-200 rounded-xl">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{ac.itemA}</span>
                    <span className="text-xl font-black text-slate-900 font-mono">{(driverStats[reviewDriver.driver_name]?.liters || 0)}{ac.itemAUnit}</span>
                  </div>
                  <div className="p-4 bg-white border border-slate-200 rounded-xl">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{ac.itemBEnabled ? ac.itemB : t("stops")}</span>
                    <span className="text-xl font-black text-slate-900 font-mono">{ac.itemBEnabled ? `${(driverStats[reviewDriver.driver_name]?.adblue || 0)}${ac.itemBUnit}` : (driverStats[reviewDriver.driver_name]?.tasks || 0)}</span>
                  </div>
                  <div className="p-4 bg-white border border-slate-200 rounded-xl">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{t("efficiency")}</span>
                    <span className="text-xl font-black text-lime-600 font-mono">{(driverStats[reviewDriver.driver_name]?.efficiency || 0)}%</span>
                  </div>
                  <div className="p-4 bg-white border border-slate-200 rounded-xl">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{t("stops")}</span>
                    <span className="text-xl font-black text-slate-900 font-mono">{(driverStats[reviewDriver.driver_name]?.sites || 0)}</span>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-200 space-y-3">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-lime-500" /> {t("direct_communication")}
                  </h4>
                  <div className="relative">
                    <textarea data-testid="ping-message-input" value={pingMessage} onChange={e => setPingMessage(e.target.value)}
                      placeholder={t("send_message_placeholder")}
                      className="w-full p-4 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 h-28 outline-none focus:border-lime-500 resize-none font-medium placeholder:text-slate-400" />
                    <button data-testid="send-ping-btn" onClick={() => handleSendPing(reviewDriver.driver_name)} disabled={!pingMessage.trim()}
                      className="absolute bottom-3 right-3 p-3 bg-lime-500 text-slate-950 rounded-xl shadow-lg disabled:opacity-30 active:scale-90 transition-all">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <Clock className="w-4 h-4 text-lime-500" /> Route History
                </h4>
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                  {(driverStats[reviewDriver.driver_name]?.tasks || []).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-8 font-bold uppercase tracking-wider">{t("no_stops_yet")}</p>
                  ) : (
                    (driverStats[reviewDriver.driver_name]?.tasks || []).slice(0, 8).map((task, i) => (
                      <div key={i} className="p-3 bg-white border border-slate-200 rounded-xl">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-bold text-slate-400 uppercase">{task.site_name || "Site"}</span>
                          <span className="text-[10px] font-mono text-slate-400">{task.completed_at ? new Date(task.completed_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : ""}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-[10px] font-bold bg-slate-100 text-slate-400 px-2 py-0.5 rounded">{task.machine_number}</span>
                          <span className="text-[10px] font-bold bg-lime-50 text-lime-600 px-2 py-0.5 rounded">+{task.delivered_diesel || 0}L</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <button data-testid="remove-driver-btn" onClick={() => handleRemoveDriver(reviewDriver.driver_name)}
                  className="w-full py-3 bg-red-50 border border-red-200 text-red-500 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-red-600 hover:text-slate-900 transition-all flex items-center justify-center gap-2">
                  <Trash2 className="w-4 h-4" /> Remove Driver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white px-4 md:px-8 py-4 flex items-center justify-between border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center text-slate-950 shadow-sm">
            <LayoutDashboard className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">{t("planner_dashboard")}</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{session?.hubName}</p>
          </div>
          <DashboardSwitcher currentRole="planner" />
        </div>
        <div className="flex items-center gap-2">
          {/* Pigeon Toolbar — animated cascade from Bird icon */}
          <div className="relative group" data-testid="pigeon-toolbar">
            <button data-testid="pigeon-toolbar-trigger"
              className="w-11 h-11 bg-red-600 hover:bg-red-500 rounded-xl flex items-center justify-center text-white shadow-lg hover:shadow-red-600/30 transition-all duration-300 hover:scale-110 active:scale-95">
              <Bird className="w-6 h-6 transition-transform duration-300 group-hover:rotate-12" />
            </button>
            {/* Cascading action buttons */}
            <div className="absolute right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[60]">
              <div className="flex flex-col gap-1.5 bg-white/95 backdrop-blur-lg border border-slate-200 rounded-xl p-2 shadow-xl min-w-[48px]">
                {[
                  { tid: "import-data-btn", icon: Upload, action: () => setShowImport(!showImport), active: showImport, color: "lime", label: "Import" },
                  { tid: "analytics-btn", icon: BarChart3, action: () => setShowAnalytics(true), color: "lime", label: "Analytics" },
                  { tid: "hub-admin-btn", icon: Users, action: () => setShowHubAdmin(true), color: "amber", label: "Admin" },
                  { tid: "calendar-btn", icon: CalendarDays, action: () => { setShowCalendar(!showCalendar); if (!showCalendar) loadCalendarAndRoutes(); }, active: showCalendar, color: "blue", notif: calNotifCount, label: "Calendar" },
                  { tid: "route-builder-btn", icon: Route, action: () => { setShowRouteBuilder(!showRouteBuilder); if (!showRouteBuilder) loadCalendarAndRoutes(); }, active: showRouteBuilder, color: "amber", label: "Routes" },
                  { tid: "pdf-export-btn", icon: FileText, color: "slate", label: "Export", isPdf: true },
                  { tid: "planner-map-btn", icon: MapPin, action: () => setShowMap(!showMap), active: showMap, color: "blue", label: "Map" },
                  { tid: "fun-request-btn", icon: Coffee, action: () => setFunMode(!funMode), active: funMode, color: "amber", label: "Fun" },
                ].map((btn, i) => {
                  const BtnIcon = btn.icon;
                  const activeClass = btn.active
                    ? `bg-${btn.color}-500 text-${btn.color === "slate" ? "white" : "slate-950"} shadow-sm`
                    : "text-slate-400 hover:text-slate-900 hover:bg-slate-50";
                  return btn.isPdf ? (
                    <div key={btn.tid} className="relative group/pdf"
                      style={{ animationDelay: `${i * 40}ms`, animation: "fadeSlideIn 0.3s ease both" }}>
                      <button data-testid={btn.tid}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeClass}`}>
                        <BtnIcon className="w-4 h-4 shrink-0" /> <span>{btn.label}</span>
                      </button>
                      <div className="absolute right-full top-0 mr-1 hidden group-hover/pdf:block">
                        <div className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden min-w-[130px]">
                          <button data-testid="pdf-sites" onClick={() => generatePDF("sites")} className="w-full px-3 py-2.5 text-left text-xs font-bold text-slate-400 hover:bg-slate-50 flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 text-lime-500" /> Sites
                          </button>
                          <button data-testid="pdf-machines" onClick={() => generatePDF("machines")} className="w-full px-3 py-2.5 text-left text-xs font-bold text-slate-400 hover:bg-slate-50 flex items-center gap-2">
                            <HardHat className="w-3.5 h-3.5 text-amber-500" /> Machines
                          </button>
                          <button data-testid="pdf-drivers" onClick={() => generatePDF("drivers")} className="w-full px-3 py-2.5 text-left text-xs font-bold text-slate-400 hover:bg-slate-50 flex items-center gap-2">
                            <Truck className="w-3.5 h-3.5 text-red-500" /> Drivers
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button key={btn.tid} data-testid={btn.tid} onClick={btn.action}
                      style={{ animationDelay: `${i * 40}ms`, animation: "fadeSlideIn 0.3s ease both" }}
                      className={`relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeClass}`}>
                      <BtnIcon className="w-4 h-4 shrink-0" /> <span>{btn.label}</span>
                      {btn.notif > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[8px] font-black flex items-center justify-center rounded-full">{btn.notif}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <TutorialToggle onClick={() => setShowTutorial(!showTutorial)} />
          <LanguageSwitcher compact />
          <button data-testid="planner-refresh-btn" onClick={handleRefresh}
            className={`p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all ${isRefreshing ? "animate-spin-slow" : ""}`}>
            <RefreshCw className="w-4 h-4" />
          </button>
          <button data-testid="back-to-lobby-btn" onClick={() => { setSession(null); nav("/portal"); }}
            className="p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button data-testid="planner-exit-btn" onClick={() => { logout(); nav("/"); }}
            className="p-2.5 bg-red-50 border border-red-200 rounded-xl text-red-500 hover:bg-red-600 hover:text-slate-900 transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Fun Request Bar */}
      {funMode && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 md:px-8 py-3 flex items-center gap-3 animate-slideInDown">
          <Sparkles className="w-5 h-5 text-amber-500 shrink-0" />
          <input data-testid="fun-request-input" value={funText} onChange={e => setFunText(e.target.value)}
            placeholder={t("fun_placeholder")} className="flex-1 bg-transparent text-sm text-slate-900 font-bold placeholder:text-amber-500/50 outline-none" />
          <button data-testid="send-fun-request-btn" onClick={handleFunRequest} disabled={!funText.trim()}
            className="px-4 py-2 bg-amber-500 text-slate-950 rounded-xl text-xs font-bold uppercase disabled:opacity-30 active:scale-95 transition-all">
            Send
          </button>
        </div>
      )}

      {/* Local Map */}
      {showMap && (
        <div className="px-4 md:px-8 py-4 bg-slate-50/80 border-b border-slate-200 animate-slideInDown" data-testid="planner-map-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-blue-500" /> Site & Asset Map
            </h3>
            <button onClick={() => setShowMap(false)} className="text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
            <LiveMap sites={sites} machines={machines} requests={requests} />
          </div>
        </div>
      )}

      {/* Import Data Panel */}
      {showImport && (
        <div className="bg-slate-50/80 border-b border-slate-200 px-4 md:px-8 py-4 animate-slideInDown" data-testid="import-panel">
          <div className="max-w-2xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Upload className="w-4 h-4 text-lime-500" /> Philogic Data Import
              </h3>
              <button onClick={() => setShowImport(false)} className="text-slate-400 hover:text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <DataImport hubId={session?.hubId} onImportComplete={() => { refreshData(); showToast("Data imported to hub!"); }} />
          </div>
        </div>
      )}

      {/* Calendar Panel */}
      {showCalendar && (
        <div className="bg-slate-50/80 border-b border-blue-500/20 px-4 md:px-8 py-4 animate-slideInDown" data-testid="calendar-panel">
          <div className="max-w-4xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-blue-500" /> Hub Calendar
              </h3>
              <button onClick={() => setShowCalendar(false)} className="text-slate-400 hover:text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            {/* Create Event Form */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
              <select value={calForm.event_type} onChange={e => setCalForm(p => ({ ...p, event_type: e.target.value }))} data-testid="cal-type"
                className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400 col-span-1">
                <option value="APPOINTMENT">Appointment</option>
                <option value="TRAINING">Training</option>
                <option value="TOOLBOX">Toolbox</option>
                <option value="LEAVE_REQUEST">Leave Request</option>
              </select>
              <input value={calForm.title} onChange={e => setCalForm(p => ({ ...p, title: e.target.value }))} placeholder="Title"
                className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400 col-span-2" data-testid="cal-title" />
              <input type="date" value={calForm.date} onChange={e => setCalForm(p => ({ ...p, date: e.target.value }))}
                className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400" data-testid="cal-date" />
              <input type="time" value={calForm.time} onChange={e => setCalForm(p => ({ ...p, time: e.target.value }))}
                className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400" data-testid="cal-time" />
              <button onClick={handleCreateCalEvent} disabled={!calForm.title || !calForm.date} data-testid="cal-create-btn"
                className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded-xl px-3 py-2.5 text-xs font-bold uppercase active:scale-95 transition-all">
                Add
              </button>
            </div>
            {/* Toolbox Upload */}
            <div className="mb-4">
              <label className="flex items-center gap-2 px-4 py-2.5 bg-white border border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-blue-500/40 transition-all" data-testid="toolbox-upload">
                <Upload className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-bold text-slate-400">Upload Toolbox Document (PDF)</span>
                <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={e => e.target.files[0] && handleToolboxUpload(e.target.files[0])} />
              </label>
            </div>
            {/* Events List */}
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {calendarEvents.length === 0 ? (
                <p className="text-xs text-slate-400 font-bold text-center py-6 uppercase">No events yet</p>
              ) : calendarEvents.map(evt => (
                <div key={evt.id} className={`p-3 rounded-xl border flex items-center justify-between ${
                  evt.event_type === "LEAVE_REQUEST" && evt.status === "PENDING" ? "border-red-500/30 bg-red-500/5" :
                  evt.event_type === "TOOLBOX" ? "border-amber-200 bg-amber-50" :
                  evt.event_type === "TRAINING" ? "border-blue-500/20 bg-blue-500/5" :
                  "border-slate-200 bg-white"
                }`} data-testid={`cal-event-${evt.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                        evt.event_type === "LEAVE_REQUEST" ? "bg-red-500/10 text-red-400" :
                        evt.event_type === "TOOLBOX" ? "bg-amber-50 text-amber-400" :
                        evt.event_type === "TRAINING" ? "bg-blue-500/10 text-blue-400" :
                        "bg-slate-100 text-slate-400"
                      }`}>{evt.event_type}</span>
                      <span className="text-xs font-bold text-slate-800">{evt.title}</span>
                      {evt.status === "PENDING" && <span className="text-[9px] font-bold text-red-400 animate-pulse">PENDING</span>}
                      {evt.status === "APPROVED" && evt.event_type === "LEAVE_REQUEST" && <span className="text-[9px] font-bold text-lime-600">APPROVED</span>}
                      {evt.status === "REJECTED" && <span className="text-[9px] font-bold text-red-500">REJECTED</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] text-slate-400 font-mono">{evt.date}{evt.time ? ` ${evt.time}` : ""}</span>
                      {evt.created_by && <span className="text-[10px] text-slate-400">by {evt.created_by}</span>}
                      {evt.file_name && <span className="text-[10px] text-amber-400 flex items-center gap-1"><FileText className="w-2.5 h-2.5" />{evt.file_name}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1.5 ml-2">
                    {evt.event_type === "LEAVE_REQUEST" && evt.status === "PENDING" && (
                      <>
                        <button onClick={() => handleApproveLeave(evt.id, "APPROVED")} data-testid={`approve-leave-${evt.id}`}
                          className="px-3 py-1.5 bg-lime-500 text-slate-950 rounded text-[10px] font-bold active:scale-95">Approve</button>
                        <button onClick={() => handleApproveLeave(evt.id, "REJECTED")} data-testid={`reject-leave-${evt.id}`}
                          className="px-3 py-1.5 bg-red-600 text-white rounded text-[10px] font-bold active:scale-95">Reject</button>
                      </>
                    )}
                    <button onClick={() => handleDeleteCalEvent(evt.id)} className="p-1.5 text-slate-400 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Route Builder Panel */}
      {showRouteBuilder && (
        <div className="bg-slate-50/80 border-b border-amber-200 px-4 md:px-8 py-4 animate-slideInDown" data-testid="route-builder-panel">
          <div className="max-w-4xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Route className="w-4 h-4 text-amber-500" /> Route Planning
              </h3>
              <button onClick={() => setShowRouteBuilder(false)} className="text-slate-400 hover:text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            {/* Build New Route */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <input value={routeName} onChange={e => setRouteName(e.target.value)} placeholder="Route name (optional)"
                className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400" data-testid="route-name-input" />
              <select value={routeDriver} onChange={e => setRouteDriver(e.target.value)} data-testid="route-driver-select"
                className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400">
                <option value="">Select Driver</option>
                {drivers.map(d => <option key={d.driver_name} value={d.driver_name}>{d.driver_name}</option>)}
              </select>
              <button onClick={handleCreateRoute} disabled={!routeDriver || routeSites.length === 0} data-testid="create-route-btn"
                className="bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 text-slate-950 rounded-xl px-3 py-2.5 text-xs font-black uppercase active:scale-95 transition-all flex items-center justify-center gap-2">
                <Send className="w-3.5 h-3.5" /> Assign Route ({routeSites.length} sites)
              </button>
            </div>
            {/* Site Selection */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {sites.map(s => {
                const isSelected = routeSites.includes(s.id);
                return (
                  <button key={s.id} onClick={() => setRouteSites(prev => isSelected ? prev.filter(x => x !== s.id) : [...prev, s.id])}
                    data-testid={`route-site-${s.id}`}
                    className={`px-3 py-2 rounded-xl text-[10px] font-bold uppercase border transition-all ${
                      isSelected ? "bg-amber-50 border-amber-400 text-amber-400" : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                    }`}>
                    <MapPin className="w-3 h-3 inline mr-1" />{s.name}
                  </button>
                );
              })}
            </div>
            {/* Route History / From History */}
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Route History</p>
              {routes.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No routes yet</p>
              ) : routes.map(rt => (
                <div key={rt.id} className={`p-3 rounded-xl border flex items-center justify-between ${
                  rt.status === "COMPLETED" ? "border-lime-500/20 bg-lime-500/5" : "border-slate-200 bg-white"
                }`} data-testid={`route-${rt.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-800">{rt.name || "Unnamed Route"}</span>
                      <span className="text-[9px] font-bold bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">{rt.driver_name}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${rt.status === "COMPLETED" ? "bg-lime-50 text-lime-600" : "bg-amber-50 text-amber-400"}`}>{rt.status}</span>
                    </div>
                    <span className="text-[10px] text-slate-400">{rt.site_ids?.length || 0} sites - {new Date(rt.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => { setRouteSites(rt.site_ids || []); setRouteDriver(rt.driver_name); setRouteName(rt.name || ""); }}
                      className="px-2.5 py-1.5 bg-slate-100 text-slate-400 rounded text-[10px] font-bold hover:text-slate-900" data-testid={`reuse-route-${rt.id}`}>Reuse</button>
                    <button onClick={() => handleDeleteRoute(rt.id)} className="p-1.5 text-slate-400 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 p-4 md:p-8 grid grid-cols-1 lg:grid-cols-4 gap-6 overflow-hidden">
        {/* Left: Sites */}
        <div className="lg:col-span-3 space-y-5 overflow-y-auto pr-1 max-h-[calc(100vh-160px)]">
          <LiveFeed events={events} />

          {/* Region Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
            <button data-testid="region-all" onClick={() => setActiveRegion("ALL")}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap border ${activeRegion === "ALL" ? "bg-lime-500 text-slate-950 border-lime-500" : "bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300"}`}>
              {t("all_regions")}
            </button>
            {REGIONS.map(r => (
              <button key={r} data-testid={`region-${r}`} onClick={() => setActiveRegion(r)}
                className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap border ${activeRegion === r ? "bg-lime-500 text-slate-950 border-lime-500" : "bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300"}`}>
                {r}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="planner-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder={t("search_placeholder")}
              className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-lime-400 outline-none transition-all" />
          </div>

          {/* Sites List */}
          <div className="space-y-3">
            {filteredSites.map((site, i) => {
              const sReqs = getSiteRequests(site.id);
              const sMachines = getSiteMachines(site.id);
              const hasUrgent = sReqs.some(r => r.urgency === "URGENT" && r.status !== "DONE");
              const newReqs = sReqs.filter(r => r.status === "NEW" || (r.status === "IN_PROGRESS" && r.request_type === "TRANSPORT"));
              const isExpanded = expandedSite === site.id;
              const siteHL = getSiteHighlight(site, machines, requests);

              return (
                <div key={site.id} data-testid={`site-row-${site.id}`}
                  className={`bg-slate-50 border rounded-xl overflow-hidden transition-all animate-fadeInUp ${hasUrgent ? "border-red-600/40" : siteHL ? highlightBorder(siteHL) : "border-slate-200 hover:border-slate-300"}`}
                  style={{ animationDelay: `${i * 0.05}s`, opacity: 0 }}>
                  <div role="button" tabIndex={0} onClick={() => setExpandedSite(isExpanded ? null : site.id)}
                    className="w-full p-4 flex items-center justify-between text-left cursor-pointer">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${hasUrgent ? "bg-red-50 text-red-500" : highlightIcon(siteHL)}`}>
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight">{site.name}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{site.city} / {site.region}</span>
                          {site.current_stage && (
                            <span className="text-[9px] font-bold bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded">{site.current_stage}</span>
                          )}
                          {site.gps_lat > 0 && (
                            <span className="text-[9px] font-mono text-lime-500/60 flex items-center gap-0.5"><Navigation className="w-2.5 h-2.5" /> GPS</span>
                          )}
                          {site.main_driver && (
                            <span className="text-[10px] font-bold bg-slate-100 text-slate-400 px-2 py-0.5 rounded">{site.main_driver}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {siteHL === "red" && <span className="text-[9px] font-bold bg-red-500/10 text-red-400 px-2 py-1 rounded flex items-center gap-1 animate-pulse"><Fuel className="w-3 h-3" /> {ac.requestType}</span>}
                      {siteHL === "green" && <span className="text-[9px] font-bold bg-lime-50 text-lime-600 px-2 py-1 rounded flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> SUPPLIED</span>}
                      {newReqs.length > 0 && (
                        <span className="px-2 py-1 bg-lime-50 text-lime-600 rounded text-[10px] font-bold">{newReqs.length} NEW</span>
                      )}
                      {hasUrgent && (
                        <span className="px-2 py-1 bg-red-50 text-red-500 rounded text-[10px] font-bold animate-pulse">URGENT</span>
                      )}
                      <span className="text-[10px] font-mono text-slate-400">{sMachines.length} machines</span>
                      <button onClick={e => { e.stopPropagation(); handleDeleteSite(site.id); }} className="p-1 text-slate-400 hover:text-red-400 transition-all" data-testid={`delete-site-${site.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-slate-200 p-4 space-y-3 animate-fadeIn">
                      {/* GPS + Site Info */}
                      {site.gps_lat > 0 && (
                        <a href={`https://www.google.com/maps?q=${site.gps_lat},${site.gps_lon}`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl hover:border-lime-300 transition-all" data-testid={`site-gps-${site.id}`}>
                          <Navigation className="w-3.5 h-3.5 text-lime-500" />
                          <span className="text-[10px] font-mono text-lime-600">{site.gps_lat.toFixed(5)}, {site.gps_lon.toFixed(5)}</span>
                          <span className="text-[9px] text-slate-400 ml-auto">Maps</span>
                        </a>
                      )}
                      {(site.lock_codes || site.accessibility_route) && (
                        <div className="flex gap-2">
                          {site.lock_codes && (
                            <span className="text-[10px] font-bold bg-amber-50 text-amber-400 px-2 py-1 rounded flex items-center gap-1">
                              <span className="text-slate-400">Code:</span> {site.lock_codes}
                            </span>
                          )}
                          {site.accessibility_route && (
                            <span className="text-[10px] font-bold bg-blue-500/10 text-blue-400 px-2 py-1 rounded flex items-center gap-1 truncate max-w-[200px]">
                              <span className="text-slate-400">Route:</span> {site.accessibility_route}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Stats */}
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="p-2.5 bg-white rounded-xl">
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">New</span>
                          <span className="text-lg font-black text-lime-600 font-mono">{sReqs.filter(r => r.status === "NEW").length}</span>
                        </div>
                        <div className="p-2.5 bg-white rounded-xl">
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Active</span>
                          <span className="text-lg font-black text-amber-400 font-mono">{sReqs.filter(r => r.status === "IN_PROGRESS").length}</span>
                        </div>
                        <div className="p-2.5 bg-white rounded-xl">
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Done</span>
                          <span className="text-lg font-black text-slate-900 font-mono">{sReqs.filter(r => r.status === "DONE").length}</span>
                        </div>
                        <div className="p-2.5 bg-white rounded-xl">
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Machines</span>
                          <span className="text-lg font-black text-slate-900 font-mono">{sMachines.length}</span>
                        </div>
                      </div>

                      {/* Machine List with fuel highlight */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Machines on site ({sMachines.length})</span>
                          <div className="flex items-center gap-2">
                            <button onClick={() => setSubjectDetail({ type: "site", subject: site })} className="text-[9px] font-bold text-slate-400 hover:text-amber-400 flex items-center gap-1" data-testid={`site-log-${site.id}`}>
                              <Download className="w-3 h-3" /> View Log
                            </button>
                            <button onClick={() => setShowAddMachine(showAddMachine === site.id ? null : site.id)}
                              className="text-[9px] font-bold text-lime-500 hover:text-lime-600 flex items-center gap-1" data-testid={`add-machine-btn-${site.id}`}>
                              <Plus className="w-3 h-3" /> Set Machines
                            </button>
                          </div>
                        </div>
                        {sMachines.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {sMachines.map(m => {
                              const mHL = getMachineHighlight(m, requests);
                              return (
                                <span key={m.id} onClick={() => setSubjectDetail({ type: "machine", subject: m })} className={`text-[9px] font-bold px-2 py-1 rounded flex items-center gap-1 border cursor-pointer hover:shadow-sm transition-all ${
                                  mHL ? highlightMachineBadge(mHL) :
                                  m.status_type === "DEFECT" ? "border-red-500/30 bg-red-500/10 text-red-400" :
                                  m.status_type === "TRANSPORT" ? "border-blue-500/30 bg-blue-500/10 text-blue-400" :
                                  m.status_type === "UPKEEP" ? "border-purple-500/30 bg-purple-500/10 text-purple-400" :
                                  "border-slate-200 bg-white text-slate-400"
                                }`} data-testid={`machine-badge-${m.id}`}>
                                  {mHL === "red" && <Fuel className="w-2.5 h-2.5" />}
                                  {mHL === "green" && <CheckCircle2 className="w-2.5 h-2.5" />}
                                  {!mHL && m.status_type === "DEFECT" && <Wrench className="w-2.5 h-2.5" />}
                                  {!mHL && m.status_type === "TRANSPORT" && <Truck className="w-2.5 h-2.5" />}
                                  {!mHL && m.status_type === "UPKEEP" && <Settings className="w-2.5 h-2.5" />}
                                  {m.machine_number}
                                  {m.gps_lat > 0 && <Navigation className="w-2 h-2 text-lime-500" />}
                                  <button onClick={e => { e.stopPropagation(); setSubjectDetail({ type: "machine", subject: m }); }} className="ml-0.5 text-slate-400 hover:text-amber-400" data-testid={`machine-log-${m.id}`}>
                                    <Download className="w-2.5 h-2.5" />
                                  </button>
                                  <button onClick={e => { e.stopPropagation(); handleDeleteMachine(m.id); }} className="text-slate-400 hover:text-red-400" data-testid={`delete-machine-${m.id}`}>
                                    <X className="w-2.5 h-2.5" />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {/* +Set Machines Panel (bulk import) */}
                        {showAddMachine === site.id && (
                          <div className="mt-2 p-3 bg-white border border-lime-500/20 rounded-xl space-y-2 animate-fadeIn" data-testid={`set-machines-panel-${site.id}`}>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Add machines via Philogic AI</p>
                            {/* Manual single add */}
                            <div className="flex gap-2">
                              <input value={newMachine.machine_number} onChange={e => setNewMachine(p => ({ ...p, machine_number: e.target.value }))} placeholder="Number *"
                                className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-[10px] font-bold text-slate-400 w-20" />
                              <input value={newMachine.name} onChange={e => setNewMachine(p => ({ ...p, name: e.target.value }))} placeholder="Name"
                                className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-[10px] font-bold text-slate-400 flex-1" />
                              <input value={newMachine.brand} onChange={e => setNewMachine(p => ({ ...p, brand: e.target.value }))} placeholder="Brand"
                                className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-[10px] font-bold text-slate-400 w-24" />
                              <button onClick={() => handleAddMachine(site.id)} disabled={!newMachine.machine_number.trim()}
                                className="px-3 py-1.5 bg-lime-500 text-slate-950 rounded text-[10px] font-bold disabled:bg-slate-700" data-testid={`confirm-add-machine-${site.id}`}>Add</button>
                            </div>
                            {/* Bulk paste */}
                            <textarea placeholder="Bulk paste machine list here... Philogic AI will decipher"
                              className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-[10px] font-bold text-slate-400 h-16 resize-none" data-testid={`bulk-paste-${site.id}`}
                              onKeyDown={async (e) => {
                                if (e.key === "Enter" && e.ctrlKey && e.target.value.trim()) {
                                  const text = e.target.value.trim();
                                  e.target.value = "";
                                  showToast("Philogic analyzing...");
                                  try {
                                    const res = await api.importMachines(auth?.jwt, session.hubId, site.id, { pasteText: text });
                                    showToast(`${res.imported} machines imported!`);
                                    refreshData();
                                  } catch(err) { showToast("Import failed"); }
                                }
                              }}
                            />
                            <div className="flex gap-2 items-center">
                              <label className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-dashed border-slate-200 rounded cursor-pointer hover:border-lime-400 text-[10px] font-bold text-slate-400 flex-1" data-testid={`bulk-upload-${site.id}`}>
                                <Upload className="w-3 h-3" /> Upload PDF/CSV/Photo
                                <input type="file" accept=".pdf,.csv,.jpg,.jpeg,.png,.txt" className="hidden" onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  showToast("Philogic analyzing...");
                                  try {
                                    const res = await api.importMachines(auth?.jwt, session.hubId, site.id, { file });
                                    showToast(`${res.imported} machines imported!`);
                                    refreshData();
                                  } catch(err) { showToast("Import failed"); }
                                  e.target.value = "";
                                }} />
                              </label>
                              <span className="text-[8px] text-slate-400">Ctrl+Enter to submit paste</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Planner Direct Request (no-machinist mode) */}
                      {!ac.hasMachinist && (
                        <div className="pt-2" data-testid={`direct-request-section-${site.id}`}>
                          {directReqSiteId === site.id ? (
                            <div className="p-3 bg-lime-50 border border-lime-300 rounded-xl space-y-2 animate-fadeIn" data-testid={`direct-request-form-${site.id}`}>
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-bold text-lime-700 uppercase tracking-widest flex items-center gap-1"><Send className="w-3 h-3" /> New {ac.requestLabel}</span>
                                <button onClick={() => setDirectReqSiteId(null)} className="text-slate-400 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                              </div>
                              <select value={directReqMachine} onChange={e => setDirectReqMachine(e.target.value)}
                                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold text-slate-900" data-testid={`direct-req-machine-${site.id}`}>
                                <option value="">Select machine...</option>
                                {sMachines.map(m => <option key={m.id} value={m.id}>{m.machine_number} {m.name ? `— ${m.name}` : ""}</option>)}
                              </select>
                              {ac.isTaskBased && ac.actions?.length > 0 && (
                                <select value={directReqType} onChange={e => setDirectReqType(e.target.value)}
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold text-slate-900" data-testid={`direct-req-action-${site.id}`}>
                                  <option value="">Select action...</option>
                                  {ac.actions.map((a, i) => <option key={i} value={a}>{a}</option>)}
                                </select>
                              )}
                              <input value={directReqNotes} onChange={e => setDirectReqNotes(e.target.value)} placeholder="Notes (optional)"
                                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-slate-900 placeholder:text-slate-400" data-testid={`direct-req-notes-${site.id}`} />
                              <button onClick={() => handleDirectRequest(site.id)} disabled={!directReqMachine || directReqSubmitting}
                                className="w-full py-2.5 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-200 disabled:text-slate-400 text-slate-950 rounded-lg text-xs font-black uppercase flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all"
                                data-testid={`direct-req-submit-${site.id}`}>
                                {directReqSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5" /> Create {ac.requestLabel}</>}
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => { setDirectReqSiteId(site.id); setDirectReqType(ac.requestType); }}
                              className="flex items-center gap-1.5 text-[10px] font-bold text-lime-600 hover:text-lime-500 py-1" data-testid={`direct-req-btn-${site.id}`}>
                              <PlusCircle className="w-3.5 h-3.5" /> Create {ac.requestLabel}
                            </button>
                          )}
                        </div>
                      )}

                      {/* All new requests with type indicators */}
                      {newReqs.map(req => {
                        const reqType = req.request_type || ac.requestType;
                        const isSupply = reqType === ac.requestType || reqType === "FUEL" || reqType === "SUPPLY";
                        return (
                          <div key={req.id} className={`p-3 bg-white border rounded-xl flex items-center justify-between ${
                            reqType === "TRANSPORT" ? "border-blue-500/30" : reqType === "REPARATION" ? "border-red-500/30" : reqType === "MAINTENANCE" ? "border-purple-500/30" : "border-slate-200"
                          }`} data-testid={`request-${req.id}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                                  isSupply ? "bg-lime-50 text-lime-600" :
                                  reqType === "REPARATION" ? "bg-red-500/10 text-red-400" :
                                  reqType === "MAINTENANCE" ? "bg-purple-500/10 text-purple-400" :
                                  "bg-blue-500/10 text-blue-400"
                                }`}>
                                  {isSupply && <Fuel className="w-2.5 h-2.5" />}
                                  {reqType === "REPARATION" && <Wrench className="w-2.5 h-2.5" />}
                                  {reqType === "MAINTENANCE" && <Settings className="w-2.5 h-2.5" />}
                                  {reqType === "TRANSPORT" && <ArrowRightLeft className="w-2.5 h-2.5" />}
                                  {isSupply ? ac.requestType : reqType}
                                </span>
                                <span className="text-xs font-bold text-slate-800">{req.machine_number}</span>
                              </div>
                              <span className="text-[10px] text-slate-400">{req.is_fun_request ? `Fun: ${req.fun_message}` : req.notes}</span>
                              {reqType === "TRANSPORT" && req.target_site_name && (
                                <p className="text-[10px] text-blue-400 font-bold mt-1 flex items-center gap-1">
                                  <ArrowRightLeft className="w-3 h-3" /> To: {req.target_site_name}
                                </p>
                              )}
                              {req.gps_lat > 0 && (
                                <p className="text-[9px] text-slate-400 font-mono mt-0.5 flex items-center gap-1">
                                  <Navigation className="w-2.5 h-2.5" /> {req.gps_lat.toFixed(4)}, {req.gps_lon.toFixed(4)}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1.5 ml-2">
                              {reqType === "TRANSPORT" ? (
                                req.status === "IN_PROGRESS" && req.assigned_driver ? (
                                  <button data-testid={`confirm-transport-${req.id}`} onClick={() => handleConfirmTransport(req.id)}
                                    className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-bold uppercase active:scale-95 transition-all flex items-center gap-1">
                                    <Truck className="w-3 h-3" /> Confirm Arrival
                                  </button>
                                ) : transportApproveId === req.id ? (
                                  <div className="flex items-center gap-1.5" data-testid={`transport-assign-${req.id}`}>
                                    <select value={transportDriver} onChange={e => setTransportDriver(e.target.value)}
                                      className="bg-white border border-slate-200 rounded px-2 py-1 text-[10px] font-bold text-slate-900 w-28" data-testid={`transport-driver-select-${req.id}`}>
                                      <option value="">Driver...</option>
                                      {drivers.map(d => <option key={d.driver_name} value={d.driver_name}>{d.driver_name}</option>)}
                                    </select>
                                    <button disabled={!transportDriver} onClick={async () => {
                                      await handleApprove(req.id, transportDriver);
                                      setTransportApproveId(null);
                                      setTransportDriver("");
                                    }} className="px-2 py-1 bg-blue-600 disabled:bg-slate-200 text-white disabled:text-slate-400 rounded text-[10px] font-bold" data-testid={`transport-dispatch-${req.id}`}>
                                      Dispatch
                                    </button>
                                    <button onClick={() => setTransportApproveId(null)} className="p-1 text-slate-400 hover:text-red-400"><X className="w-3 h-3" /></button>
                                  </div>
                                ) : (
                                  <button data-testid={`approve-transport-${req.id}`} onClick={() => { setTransportApproveId(req.id); setTransportDriver(site.main_driver || ""); }}
                                    className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-bold uppercase active:scale-95 transition-all flex items-center gap-1">
                                    <ArrowRightLeft className="w-3 h-3" /> Assign Driver
                                  </button>
                                )
                              ) : (
                                <button data-testid={`approve-${req.id}`} onClick={() => handleApprove(req.id, site.main_driver)}
                                  className="px-3 py-1.5 bg-lime-500 text-slate-950 rounded text-xs font-bold uppercase active:scale-95 transition-all">
                                  {t("approve")}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Site Feedback Log (color-coded) */}
                      {(() => {
                        const sfb = (feedback || []).filter(fb => fb.site_id === site.id).slice(0, 5);
                        if (sfb.length === 0) return null;
                        return (
                          <div className="pt-2 space-y-1.5" data-testid={`planner-feedback-log-${site.id}`}>
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Driver Feedback Log</span>
                            {sfb.map(fb => (
                              <div key={fb.id} className={`p-2.5 rounded border text-[11px] font-bold ${
                                fb.feedback_type === "NEGATIVE"
                                  ? "border-red-500/30 bg-red-500/5 text-red-400"
                                  : "border-lime-300 bg-lime-500/5 text-lime-600"
                              }`} data-testid={`planner-fb-${fb.id}`}>
                                <div className="flex items-center justify-between">
                                  <span className="flex items-center gap-1">
                                    {fb.feedback_type === "NEGATIVE" ? <ThumbsDown className="w-3 h-3" /> : <ThumbsUp className="w-3 h-3" />}
                                    {fb.author}
                                  </span>
                                  <span className="text-[9px] text-slate-400">{new Date(fb.date).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                                </div>
                                <p className={`mt-0.5 ${fb.feedback_type === "NEGATIVE" ? "text-red-300" : "text-lime-300"}`}>{fb.message}</p>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Fleet Intelligence */}
        <aside className="hidden lg:flex flex-col space-y-5 overflow-y-auto max-h-[calc(100vh-160px)] pr-1">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 px-1">
            <Activity className="w-4 h-4 text-lime-500" /> {t("fleet_intelligence")}
          </div>

          {/* Global Stats */}
          <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-lime-50 rounded-xl"><BarChart3 className="w-4 h-4 text-lime-500" /></div>
              <div>
                <span className="text-[10px] font-bold text-lime-500 uppercase tracking-wider block">{t("fleet_volume")}</span>
                <span className="text-xl font-black font-mono text-slate-900">{globalStats.totalLiters}L</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <span className="flex items-center gap-1"><Users className="w-3 h-3 text-lime-500" /> {globalStats.activeDrivers} {t("active_drivers")}</span>
              <span className="flex items-center gap-1"><HardHat className="w-3 h-3 text-amber-500" /> {globalStats.totalMachines} {t("refuelings")}</span>
            </div>
            {globalStats.newRequests > 0 && (
              <div className="p-2 bg-lime-50 border border-lime-500/20 rounded text-center">
                <span className="text-xs font-bold text-lime-600">{globalStats.newRequests} {t("new_requests")}</span>
              </div>
            )}
          </div>

          {/* Driver Cards */}
          {drivers.map(driver => {
            const stats = driverStats[driver.driver_name] || { liters: 0, machines: 0, sites: 0 };
            const isActive = driver.status === "ACTIVE";
            return (
              <div key={driver.driver_name} data-testid={`driver-card-${driver.driver_name}`}
                onClick={() => setReviewDriver(driver)}
                className="bg-slate-50 border border-slate-200 rounded-xl p-5 cursor-pointer hover:border-slate-300 transition-all group">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white font-black text-lg ${isActive ? "bg-lime-500 text-slate-950" : "bg-slate-700"}`}>
                      {driver.driver_name[0]}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight">{driver.driver_name}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className={`status-dot ${isActive ? "online" : "offline"}`} />
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{driver.region}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-amber-500">
                    <Hand className="w-3.5 h-3.5" />
                    <span className="text-xs font-black">{driver.high_five_count || 0}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="p-2 bg-white rounded text-center">
                    <span className="text-[9px] font-bold text-slate-400 uppercase block">{ac.itemA}</span>
                    <span className="text-sm font-black text-slate-900 font-mono">{stats.liters}{ac.itemAUnit}</span>
                  </div>
                  <div className="p-2 bg-white rounded text-center">
                    <span className="text-[9px] font-bold text-slate-400 uppercase block">{t("machines")}</span>
                    <span className="text-sm font-black text-slate-900 font-mono">{stats.machines}</span>
                  </div>
                </div>
                <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                  <button onClick={() => setReviewDriver(driver)}
                    className="flex-1 py-2 bg-white text-slate-400 rounded text-[10px] font-bold uppercase tracking-wider hover:bg-slate-100 flex items-center justify-center gap-1 transition-all">
                    <Activity className="w-3 h-3 text-lime-500" /> {t("analyze")}
                  </button>
                  <button onClick={() => handleHighFive(driver.driver_name)}
                    className="p-2 bg-amber-50 text-amber-500 rounded hover:bg-amber-500 hover:text-slate-950 transition-all active:scale-90" data-testid={`highfive-${driver.driver_name}`}>
                    <Hand className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </aside>
      </main>
    </div>
  );
}
